import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, LifeBuoy, Cpu, AlertOctagon, ArrowUpRight, Radar, Video, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/common/StatusBadge";

const SYS_ICONS = { access_control: KeyRound, vms: Video, radar: Radar };

export default function DashboardPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/meta/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  const stats = [
    { Icon: FolderKanban, label: t("dashboard.kpi.projects"), value: data?.projects?.active ?? "—", sub: `${data?.projects?.total ?? 0} total`, test: "kpi-projects" },
    { Icon: LifeBuoy,     label: t("dashboard.kpi.tickets"),  value: data?.tickets?.total ?? "—",   sub: "all time",                                test: "kpi-tickets" },
    { Icon: Cpu,          label: t("dashboard.kpi.assets"),   value: data?.hardware?.total ?? "—",  sub: `${data?.hardware?.operational ?? 0} ok`,   test: "kpi-assets" },
    { Icon: AlertOctagon, label: t("dashboard.kpi.critical"), value: data?.hardware?.critical ?? 0, sub: `${data?.hardware?.warning ?? 0} warnings`, test: "kpi-critical" },
  ];

  return (
    <div className="p-6 lg:p-10 space-y-10">
      <header>
        <div className="text-overline">{new Date().toUTCString().split(" GMT")[0]} UTC</div>
        <div className="flex items-center justify-between mt-2 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white" data-testid="dashboard-title">
              {t("dashboard.title")}
            </h1>
            <p className="mt-2 text-sm text-secondary-foreground/70">
              {t("dashboard.subtitle")} · <span className="font-mono text-primary">{user?.name}</span>
            </p>
          </div>
        </div>
      </header>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ Icon, label, value, sub, test }) => (
          <div key={label} className="p-6 border border-border rounded-sm bg-card relative overflow-hidden" data-testid={test}>
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-primary/5 rounded-full" />
            <div className="flex items-start justify-between">
              <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <span className="text-[10px] font-mono tracking-[0.24em] text-muted-foreground uppercase">{label}</span>
            </div>
            <div className="mt-6 text-4xl font-semibold text-white tracking-tight font-mono">{value}</div>
            <div className="mt-1 text-[11px] font-mono tracking-widest text-muted-foreground uppercase">{sub}</div>
          </div>
        ))}
      </section>

      <section className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 border border-border rounded-sm bg-card">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="text-overline">{t("dashboard.projects.recent")}</div>
            <Link to="/projects" className="text-xs text-primary flex items-center gap-1 font-mono tracking-widest uppercase">ALL <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          <div>
            {(data?.projects?.items || []).length === 0 ? (
              <div className="p-10 text-sm text-muted-foreground">No projects yet.</div>
            ) : (data?.projects?.items || []).map((p) => {
              const Icon = SYS_ICONS[p.system] || FolderKanban;
              return (
                <div key={p.project_id} className="flex items-center gap-4 px-6 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`dashboard-project-${p.project_id}`}>
                  <div className="w-10 h-10 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase mt-0.5">{p.site} · {p.engineer || "—"}</div>
                  </div>
                  <div className="hidden md:block w-40">
                    <div className="h-1 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${p.progress}%` }} />
                    </div>
                    <div className="mt-1 font-mono text-[10px] tracking-widest text-muted-foreground uppercase">{p.progress}% · {p.region || "—"}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="border border-border rounded-sm bg-card">
            <div className="px-6 py-4 border-b border-border text-overline">{t("dashboard.hardware.health")}</div>
            <div className="p-6 space-y-4">
              <HealthRow label={t("common.operational")} value={data?.hardware?.operational || 0} total={data?.hardware?.total || 1} color="#00E676" />
              <HealthRow label={t("common.warning")}     value={data?.hardware?.warning || 0}     total={data?.hardware?.total || 1} color="#FFC400" />
              <HealthRow label={t("common.critical")}    value={data?.hardware?.critical || 0}    total={data?.hardware?.total || 1} color="#FF3D00" />
            </div>
          </div>
          <div className="border border-border rounded-sm bg-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="text-overline">{t("dashboard.tickets.recent")}</div>
              <Link to="/tickets" className="text-xs text-primary flex items-center gap-1 font-mono tracking-widest uppercase">ALL <ArrowUpRight className="w-3 h-3" /></Link>
            </div>
            <div>
              {(data?.tickets?.recent || []).length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">{t("tickets.empty")}</div>
              ) : (data?.tickets?.recent || []).slice(0, 5).map((tk) => (
                <Link key={tk.ticket_id} to={`/tickets/${tk.ticket_id}`} className="flex items-center gap-3 px-6 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`dashboard-ticket-${tk.ticket_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono tracking-widest text-primary">{tk.ticket_id}</div>
                    <div className="text-sm text-white truncate mt-0.5">{tk.subject}</div>
                  </div>
                  <StatusBadge status={tk.status} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function HealthRow({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] font-mono tracking-widest text-muted-foreground uppercase">
        <span>{label}</span>
        <span className="text-white">{value}/{total}</span>
      </div>
      <div className="mt-2 h-1 bg-secondary rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
